import React from "react";
import { motion } from "framer-motion";
import saucerSwapLogo from "../assets/Larry.svg";

interface SwapButtonsProps {
  variant?: "default" | "compact";
  className?: string;
  title?: string;
  subtitle?: string;
}

const SwapButtons: React.FC<SwapButtonsProps> = ({
  variant = "default",
  className = "",
  title = "Trade Tokens",
  subtitle = "Buy tokens on decentralized exchanges",
}) => {
  const isCompact = variant === "compact";

  return (
    <motion.div
      className={`flex flex-col items-center gap-4 ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Section Title */}
      <div className="text-center">
        <h3
          className={`font-semibold text-gray-800 mb-2 ${
            isCompact ? "text-lg" : "text-xl"
          }`}
        >
          {title}
        </h3>
        <p className={`text-gray-600 ${isCompact ? "text-sm" : "text-base"}`}>
          {subtitle}
        </p>
      </div>

      {/* SaucerSwap - Hedera */}
      <motion.div
        className="w-full"
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.2 }}
      >
        <a
          href="https://www.saucerswap.finance/swap/HBAR/0.0.1991880"
          target="_blank"
          rel="noopener noreferrer"
          className={`
            flex items-center justify-center gap-3 w-full
            bg-gradient-to-r from-teal-50 to-blue-50 
            hover:from-teal-100 hover:to-blue-100
            border-2 border-teal-200 hover:border-teal-300
            rounded-xl transition-all duration-300 shadow-sm hover:shadow-md
            ${isCompact ? "p-3" : "p-4"}
          `}
        >
          <img
            src={saucerSwapLogo}
            alt="SaucerSwap - Decentralized exchange on Hedera for trading ASSET tokens"
            className={`${isCompact ? "h-8" : "h-10"} w-auto`}
            loading="lazy"
            width="auto"
            height={isCompact ? 32 : 40}
          />
          <div className="text-left">
            <div
              className={`font-semibold text-teal-800 ${
                isCompact ? "text-sm" : "text-base"
              }`}
            >
              ASSET (HTS)
            </div>
            <div
              className={`text-teal-600 ${isCompact ? "text-xs" : "text-sm"}`}
            >
              Trade on Hedera
            </div>
          </div>
        </a>
      </motion.div>

      {/* PancakeSwap - BSC Tokens */}
      <div className="w-full space-y-3">
        <div
          className={`text-center text-gray-700 font-medium ${
            isCompact ? "text-sm" : "text-base"
          }`}
        >
          BSC Network
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* NBM Token */}
          <motion.a
            href="https://pancakeswap.finance/swap?inputCurrency=BNB&outputCurrency=0x12Da2f2761038486271C99DA7e0FB4413e2B5E38"
            target="_blank"
            rel="noopener noreferrer"
            className={`
              flex flex-col items-center justify-center
              bg-gradient-to-br from-purple-50 to-purple-100
              hover:from-purple-100 hover:to-purple-150
              border-2 border-purple-200 hover:border-purple-300
              rounded-xl transition-all duration-300 shadow-sm hover:shadow-md
              group ${isCompact ? "p-3" : "p-4"}
            `}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div
              className={`font-semibold text-purple-800 group-hover:text-purple-900 ${
                isCompact ? "text-sm" : "text-base"
              }`}
            >
              NBM
            </div>
            <div
              className={`text-purple-600 group-hover:text-purple-700 ${
                isCompact ? "text-xs" : "text-sm"
              }`}
            >
              PancakeSwap
            </div>
            <div
              className={`text-purple-500 mt-1 ${
                isCompact ? "text-xs" : "text-sm"
              }`}
            >
              1000 NBM = 1 ASSET
            </div>
          </motion.a>

          {/* ASSET Token */}
          <motion.a
            href="https://pancakeswap.finance/swap?inputCurrency=BNB&outputCurrency=0x6b471d5ab9f3d92a600e7d49a0b135bf6d4c6a5b"
            target="_blank"
            rel="noopener noreferrer"
            className={`
              flex flex-col items-center justify-center
              bg-gradient-to-br from-orange-50 to-orange-100
              hover:from-orange-100 hover:to-orange-150
              border-2 border-orange-200 hover:border-orange-300
              rounded-xl transition-all duration-300 shadow-sm hover:shadow-md
              group ${isCompact ? "p-3" : "p-4"}
            `}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div
              className={`font-semibold text-orange-800 group-hover:text-orange-900 ${
                isCompact ? "text-sm" : "text-base"
              }`}
            >
              ASSET
            </div>
            <div
              className={`text-orange-600 group-hover:text-orange-700 ${
                isCompact ? "text-xs" : "text-sm"
              }`}
            >
              PancakeSwap
            </div>
            <div
              className={`text-orange-500 mt-1 ${
                isCompact ? "text-xs" : "text-sm"
              }`}
            >
              1:1 with HTS
            </div>
          </motion.a>
        </div>
      </div>
    </motion.div>
  );
};

export default SwapButtons;
